// =====================================================================
// Operation reducer (§2 + §3)
// =====================================================================
// 仕様準拠: closing_river_v9_spec.md
//   §2 大→小引く / 同値合流
//   §3 合流処理 順序固定:
//     1. 操作辺 e を active→history (consumed)
//     2. 新ノード w を生成、value(w) = value(u) (= value(v))
//     3. u, v に接続する他 active 辺を w に rewire
//        - (u,v) 等の重複辺は (w,w) 自己ループ化
//     4. 自己ループ化した辺を active→history (self_loop) すべて
//     5. 旧ノード u, v を削除
//
// 重要:
//   - 1手 = 1辺消費 (合流に伴う self_loop 削除は手数に数えない)
//   - Clean event 判定 (§9): self_loop 削除が 1本以上で true
// =====================================================================
import { addNode, incidentEdges, moveEdgeToHistory, removeNode, rewireEdge, setNodeValue, withChanges, } from './boardState.js';
/**
 * 操作: active 辺 edgeId に対し §2 / §3 を適用。
 * 不正な辺 / 不正な状態は throw する。
 * 勝敗判定は呼び出し側 (judge.ts) で行う。
 */
export function applyOperation(state, edgeId) {
    const edge = state.activeEdges.get(edgeId);
    if (!edge)
        throw new Error(`applyOperation: edge ${edgeId} not in active_edges`);
    const u = state.nodes.get(edge.src);
    const v = state.nodes.get(edge.dst);
    if (!u)
        throw new Error(`applyOperation: src node ${edge.src} not found`);
    if (!v)
        throw new Error(`applyOperation: dst node ${edge.dst} not found`);
    if (edge.src === edge.dst) {
        throw new Error(`applyOperation: edge ${edgeId} is self-loop (invariant violation)`);
    }
    // step advance (history 用)
    let next = withChanges(state, {
        currentStep: state.currentStep + 1,
    });
    if (u.value === v.value) {
        // 合流処理 §3
        return applyMerge(next, edgeId, edge.src, edge.dst);
    }
    else {
        // 減算処理 §2
        return applySubtract(next, edgeId, u.id, u.value, v.id, v.value);
    }
}
/** §2 減算 */
function applySubtract(state, edgeId, uId, uVal, vId, vVal) {
    // 大→小引く
    let next = state;
    if (uVal > vVal) {
        next = setNodeValue(next, uId, uVal - vVal);
    }
    else {
        // vVal > uVal
        next = setNodeValue(next, vId, vVal - uVal);
    }
    // 辺 e を history (consumed)
    next = moveEdgeToHistory(next, edgeId, 'consumed');
    // 手数 +1
    next = withChanges(next, { movesUsed: next.movesUsed + 1 });
    return {
        nextState: next,
        cleanEvent: false,
        consumedEdgeId: edgeId,
        selfLoopEdgeIds: [],
        operationKind: 'subtract',
    };
}
/** §3 合流処理 (順序固定) */
function applyMerge(state, edgeId, uId, vId) {
    const uNode = state.nodes.get(uId);
    const vNode = state.nodes.get(vId);
    if (uNode.value !== vNode.value) {
        throw new Error(`applyMerge: precondition violated value(u)=${uNode.value}, value(v)=${vNode.value}`);
    }
    const mergedValue = uNode.value;
    // === Step 1: 操作辺 e を active→history (consumed) ===
    let next = moveEdgeToHistory(state, edgeId, 'consumed');
    // === Step 2: 新ノード w を生成 ===
    const added = addNode(next, mergedValue);
    next = added.state;
    const wId = added.id;
    // === Step 3: u, v に接続する他 active 辺を w に rewire ===
    // 注意: incidentEdges は active_edges のスナップショットから取るが、
    // 既に Step 1 で操作辺は active から消えているので含まれない。
    // edge_id 昇順で決定論的に処理。
    const incidentToU = incidentEdges(next, uId);
    const incidentToV = incidentEdges(next, vId);
    // u と v の両方に接続する辺 (= e 以外の (u,v) 重複辺) は両方の incident に出てくる。
    // edge は一意なので重複は除外しつつ rewire する。
    const toRewire = new Set();
    for (const id of incidentToU)
        toRewire.add(id);
    for (const id of incidentToV)
        toRewire.add(id);
    const rewireOrder = Array.from(toRewire).sort((a, b) => a - b);
    for (const eid of rewireOrder) {
        const e = next.activeEdges.get(eid);
        const newSrc = e.src === uId || e.src === vId ? wId : e.src;
        const newDst = e.dst === uId || e.dst === vId ? wId : e.dst;
        next = rewireEdge(next, eid, newSrc, newDst);
    }
    // === Step 4: 自己ループ化した辺をすべて active→history (self_loop) ===
    const selfLoopIds = [];
    for (const eid of rewireOrder) {
        const e = next.activeEdges.get(eid);
        if (!e)
            continue; // 既に history 入りした (起きないはずだが safety)
        if (e.src === wId && e.dst === wId) {
            selfLoopIds.push(eid);
        }
    }
    for (const eid of selfLoopIds) {
        next = moveEdgeToHistory(next, eid, 'self_loop');
    }
    // === Step 5: 旧ノード u, v を削除 ===
    next = removeNode(next, uId);
    next = removeNode(next, vId);
    // === 手数 +1 (合流処理全体で1手) ===
    next = withChanges(next, { movesUsed: next.movesUsed + 1 });
    return {
        nextState: next,
        cleanEvent: selfLoopIds.length > 0,
        consumedEdgeId: edgeId,
        selfLoopEdgeIds: selfLoopIds,
        operationKind: 'merge',
    };
}
/**
 * 不変条件チェック (debug 用):
 *   - 自己ループが active に存在しない
 *   - ノード値が 1..99
 *   - active 辺の端点が両方 active ノードに存在
 */
export function assertInvariants(state) {
    for (const n of state.nodes.values()) {
        if (!Number.isInteger(n.value) || n.value < 1 || n.value > 99) {
            throw new Error(`invariant: node ${n.id} has invalid value ${n.value}`);
        }
    }
    for (const e of state.activeEdges.values()) {
        if (e.src === e.dst) {
            throw new Error(`invariant: active edge ${e.id} is self-loop`);
        }
        if (!state.nodes.has(e.src)) {
            throw new Error(`invariant: edge ${e.id} references missing src ${e.src}`);
        }
        if (!state.nodes.has(e.dst)) {
            throw new Error(`invariant: edge ${e.id} references missing dst ${e.dst}`);
        }
    }
}
//# sourceMappingURL=operation.js.map